import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * The broadcast team's timing screen: what is happening, what is worth
 * following, and what it means for the championships.
 *
 * Purely a view. It is given the bridge's read-only view (services/timingBridge/
 * view.js) and has no controls that reach the bridge — nothing here can change
 * a data stream or a graphic. Used by the in-app page and by the share link
 * for commentators, so it assumes no login and no Mission Control chrome.
 */

const FLAG = {
  green: { label: 'GREEN', cls: 'bg-green-600 text-white' },
  yellow: { label: 'YELLOW', cls: 'bg-yellow-400 text-black' },
  fcy: { label: 'FULL COURSE YELLOW', cls: 'bg-yellow-400 text-black' },
  sc: { label: 'SAFETY CAR', cls: 'bg-yellow-400 text-black' },
  red: { label: 'RED FLAG', cls: 'bg-red-600 text-white' },
  finish: { label: 'CHEQUERED', cls: 'bg-white text-black' },
  stop: { label: 'STOPPED', cls: 'bg-red-600 text-white' },
  none: { label: '-', cls: 'bg-[#222] text-gray-400' },
};

function Flag({ flag }) {
  const f = FLAG[flag] || FLAG.none;
  return <span className={`px-2.5 py-1 rounded text-xs font-bold tracking-wider ${f.cls}`}>{f.label}</span>;
}

function Change({ change }) {
  if (!change) return <span className="inline-block w-6" />;
  return (
    <span className={`inline-block w-6 text-[10px] font-semibold ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
      {change > 0 ? '▲' : '▼'}{Math.abs(change)}
    </span>
  );
}

/** The number plate, in the class colour when there is one. */
function Plate({ no, bg, fg }) {
  return (
    <span className="inline-block min-w-[2.25rem] px-1 text-center rounded-sm font-bold tabular-nums" style={bg ? { background: bg, color: fg || '#000' } : { background: '#1f1f1f', color: '#fff' }}>
      {no}
    </span>
  );
}

// --- Live timing -----------------------------------------------------------------

const ordinal = (n) => `P${n}`;

function RunningOrder({ view, highlight }) {
  const liveChamps = view.championships.filter(c => c.live);
  const rowRefs = useRef(new Map());

  // Bring a battle picked on the right into the middle of the screen: the
  // midpoint of the pair, centred top to bottom.
  useEffect(() => {
    const rects = [...highlight].map(no => rowRefs.current.get(no)?.getBoundingClientRect()).filter(Boolean);
    if (!rects.length) return;
    const top = Math.min(...rects.map(r => r.top));
    const bottom = Math.max(...rects.map(r => r.bottom));
    window.scrollTo({ top: window.scrollY + (top + bottom) / 2 - window.innerHeight / 2, behavior: 'smooth' });
  }, [highlight]);

  return (
    <div className="border border-[#262626] rounded overflow-hidden overflow-x-auto self-start">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-[#161616] text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-2 py-2 text-left w-16">Pos</th>
            <th className="px-2 py-2 text-left w-14">No</th>
            <th className="px-2 py-2 text-left">Rider</th>
            <th className="px-2 py-2 text-right w-12">Laps</th>
            <th className="px-2 py-2 text-right w-20">Gap</th>
            <th className="px-2 py-2 text-right w-20">Int</th>
            <th className="px-2 py-2 text-right w-24">Last</th>
            <th className="px-2 py-2 text-right w-24">Best</th>
            {liveChamps.map(c => (
              <React.Fragment key={c.key}>
                <th className="pl-4 pr-2 py-2 text-left border-l border-[#262626] whitespace-nowrap w-28" title={`Place in the ${c.label} championship if the race ended now`}>{c.label} champ</th>
                <th className="px-2 py-2 text-right w-14" title={c.dayScored ? 'Championship points before today' : 'Championship points before this race'}>{c.dayScored ? 'Before' : 'Had'}</th>
                <th className="px-2 py-2 text-right w-12" title={c.dayScored ? "Championship points for today's day position so far" : 'Points for where they are in this race'}>{c.dayScored ? 'Today' : 'Race'}</th>
                <th className="px-2 py-2 text-right w-14" title="Championship points if the race ended now">Live</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {view.order.map((r, i) => {
            const contender = r.champs.some(c => c.pos <= 5);
            const lit = highlight.has(r.no);
            return (
              <tr
                key={`${r.no}-${i}`}
                ref={el => { if (el) rowRefs.current.set(r.no, el); else rowRefs.current.delete(r.no); }}
                className={`border-t border-[#1f1f1f] ${lit ? 'bg-yellow-500/15 outline outline-1 outline-yellow-500/70' : contender ? 'bg-blue-950/25' : i % 2 ? 'bg-[#0d0d0d]' : ''}`}
              >
                <td className="px-2 py-1 tabular-nums text-white font-semibold whitespace-nowrap">{r.pos ?? '-'} <Change change={r.change} /></td>
                <td className="px-2 py-1"><Plate no={r.no} bg={r.clsBg} fg={r.clsFg} /></td>
                <td className="px-2 py-1 text-gray-100 truncate max-w-[16rem]">
                  {r.name}
                  {r.pit && <span className="ml-2 text-[10px] px-1 rounded bg-amber-500/20 text-amber-400 font-semibold">PIT</span>}
                  {r.status && r.status !== 'pit' && <span className="ml-2 text-[10px] text-gray-500 uppercase">{r.status}</span>}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-gray-400">{r.laps ?? ''}</td>
                <td className="px-2 py-1 text-right tabular-nums text-gray-200">{r.gap}</td>
                <td className="px-2 py-1 text-right tabular-nums text-gray-400">{r.interval}</td>
                <td className="px-2 py-1 text-right tabular-nums text-gray-400">{r.lastLap}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${r.fastest ? 'text-purple-400 font-semibold' : 'text-gray-400'}`}>{r.bestLap}</td>
                {liveChamps.map((champ) => {
                  const c = r.champs.find(x => x.key === champ.key);
                  if (!c) return <td key={champ.key} colSpan={4} className="pl-4 pr-2 py-1 border-l border-[#262626] text-gray-700">-</td>;
                  const top = c.pos <= 5;
                  return (
                    <React.Fragment key={champ.key}>
                      <td className={`pl-4 pr-2 py-1 tabular-nums whitespace-nowrap border-l border-[#262626] font-semibold ${top ? 'text-blue-300' : 'text-gray-400'} ${c.title === 'out' ? 'opacity-60' : ''}`}>
                        {ordinal(c.pos)} <Change change={c.change} />
                        {c.title === 'champion' && <span className="ml-1 text-[10px] px-1 rounded bg-yellow-400 text-black font-bold">CHAMPION</span>}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums text-gray-400">{c.before}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-green-400 whitespace-nowrap">
                        {c.race ? `+${c.race}` : ''}
                        {champ.dayScored && c.dayPos != null && <span className="ml-1 text-[10px] text-gray-500">D{c.dayPos}</span>}
                      </td>
                      <td className={`px-2 py-1 text-right tabular-nums font-semibold ${top ? 'text-white' : 'text-gray-300'}`}>{c.points}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One side of a battle: who, and what they have riding on the championship. */
function BattleRider({ rider, role }) {
  const stake = rider.standings.find(s => s.pos <= 5) || rider.standings[0] || null;
  const scoring = stake && (stake.before > 0 || stake.race > 0 || stake.inTable);
  return (
    <div className={`min-w-0 flex-1 ${role === 'target' ? 'text-right' : ''}`}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{role === 'chaser' ? `P${rider.pos} · chasing` : `P${rider.pos} · ahead`}</div>
      <div className="text-sm font-bold text-white truncate"><span className="text-gray-500 tabular-nums font-normal mr-1">{rider.no}</span>{rider.short}</div>
      <div className="mt-0.5">
        {rider.contender ? (
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-600 text-white">
            {rider.standings.filter(s => s.pos <= 5).map(s => `${s.label} P${s.pos}`).join(' · ')} · title contender
          </span>
        ) : scoring ? (
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-[#1f1f1f] text-gray-400">{stake.label} P{stake.pos}</span>
        ) : (
          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-[#1a1a1a] text-gray-500">no championship stake</span>
        )}
      </div>
    </div>
  );
}

function BattleTrend({ battle }) {
  const { trend, summary } = battle;
  if (!trend) return <div className="text-[11px] text-gray-600 mt-1.5">Trend after a couple more laps</div>;
  const style = trend === 'closing'
    ? { icon: '▶▶', cls: 'text-green-300 bg-green-900/40 border-green-700' }
    : trend === 'pulling'
      ? { icon: '◀◀', cls: 'text-red-300 bg-red-900/30 border-red-800' }
      : { icon: '=', cls: 'text-gray-300 bg-[#1a1a1a] border-[#2a2a2a]' };
  return (
    <div className={`mt-1.5 px-2 py-1 rounded border text-sm font-semibold ${style.cls}`}>
      <span className="mr-1.5">{style.icon}</span>{summary}
    </div>
  );
}

function Battles({ battles, selected, onSelect }) {
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Worth following <span className="normal-case tracking-normal text-gray-600">(click one to find it in the timing)</span></h3>
      {battles.length === 0 && <p className="text-sm text-gray-600">No same-lap battles within 1.5s.</p>}
      <div className="space-y-1.5">
        {battles.slice(0, 8).map((b) => {
          const id = `${b.chaser.no}-${b.target.no}`;
          const on = selected === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(on ? null : id, on ? [] : [b.chaser.no, b.target.no])}
              className={`w-full text-left px-3 py-2 rounded border transition-colors ${
                on ? 'border-yellow-500 bg-yellow-500/10' : b.titleFight ? 'border-blue-700 bg-blue-950/30 hover:border-blue-500' : 'border-[#262626] bg-[#121212] hover:border-[#3a3a3a]'
              }`}
            >
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>for P{b.forPosition}</span>
                <span className="text-lg font-bold tabular-nums text-white">{b.gap}</span>
              </div>
              <div className="flex items-start gap-2 mt-0.5">
                <BattleRider rider={b.chaser} role="chaser" />
                <span className="self-center text-gray-500 text-lg">→</span>
                <BattleRider rider={b.target} role="target" />
              </div>
              <BattleTrend battle={b} />
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ChampionshipLive({ champ }) {
  return (
    <section className="rounded border border-[#262626] bg-[#121212] px-3 py-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold text-white">{champ.label}</span>
        <span className="text-[10px] text-green-400 font-semibold">IF IT ENDED NOW</span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wide text-gray-600">
          <tr>
            <th className="text-left font-normal" colSpan={2} />
            <th className="text-right font-normal" title={champ.dayScored ? 'Championship points before today' : 'Championship points before this race'}>{champ.dayScored ? 'Before' : 'Had'}</th>
            <th className="text-right font-normal" title={champ.dayScored ? "Championship points for today's day position so far" : 'Points for where they are in this race'}>{champ.dayScored ? 'Today' : 'Race'}</th>
            <th className="text-right font-normal">Live</th>
            <th className="text-right font-normal">Gap</th>
          </tr>
        </thead>
        <tbody>
          {champ.table.slice(0, 5).map(r => (
            <tr key={r.no} className="border-t border-[#1f1f1f]">
              <td className="py-0.5 w-12 tabular-nums text-white font-semibold whitespace-nowrap">{r.pos}{r.tied && '='} <Change change={r.change} /></td>
              <td className="py-0.5 text-gray-100 truncate max-w-[9rem]">
                <span className="text-gray-500 tabular-nums mr-1">{r.no}</span>{r.short}
                {!r.inRace && <span className="ml-1 text-[10px] text-amber-500">not in race</span>}
              </td>
              <td className="py-0.5 text-right tabular-nums text-gray-400">{r.before}</td>
              <td className="py-0.5 text-right tabular-nums text-green-400 whitespace-nowrap">
                {champ.dayScored
                  ? <>{r.race ? `+${r.race}` : ''}{r.dayPos != null && <span className="text-[10px] text-gray-500 ml-0.5">Day P{r.dayPos}</span>}</>
                  : <>{r.inRace ? `+${r.race}` : ''}{r.inRace && <span className="text-[10px] text-gray-500 ml-0.5">P{r.racePos}</span>}</>}
              </td>
              <td className="py-0.5 text-right tabular-nums text-white font-semibold">{r.points}</td>
              <td className="py-0.5 text-right tabular-nums text-gray-500 w-12">{r.gap ? `-${r.gap}` : r.pos > 1 ? 'level' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-gray-500 mt-1">{champ.headline}</p>
    </section>
  );
}

function Moments({ events }) {
  if (!events.length) return null;
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Moments</h3>
      <div className="space-y-1">
        {events.slice(0, 10).map(e => (
          <div key={e.id} className="flex items-center gap-2 text-xs">
            <span className="text-gray-600 tabular-nums w-16">{new Date(e.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            <span className="text-[10px] uppercase tracking-wide text-blue-400 w-20">{e.type}</span>
            <span className="text-gray-200">{e.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function LiveTimingTab({ view }) {
  const live = view.championships.filter(c => c.live);
  const [picked, setPicked] = useState({ id: null, nos: [] });
  // A battle that has broken up stops being highlighted.
  const stillOn = picked.id && view.battles.some(b => `${b.chaser.no}-${b.target.no}` === picked.id);
  const highlight = useMemo(() => new Set(stillOn ? picked.nos : []), [stillOn, picked]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
      <RunningOrder view={view} highlight={highlight} />
      <div className="space-y-4">
        {live.map(c => <ChampionshipLive key={c.key} champ={c} />)}
        {!live.length && view.championships.length > 0 && (
          <p className="text-xs text-gray-600">The session on track is not one of the championship races ({view.championships.map(c => c.label).join(', ')}).</p>
        )}
        <Battles battles={view.battles} selected={stillOn ? picked.id : null} onSelect={(id, nos) => setPicked({ id, nos })} />
        <Moments events={view.events} />
      </div>
    </div>
  );
}

// --- Key info --------------------------------------------------------------------

const TITLE_BADGE = {
  champion: 'bg-yellow-400 text-black',
  alive: 'bg-green-900/50 text-green-300',
  out: 'bg-[#1f1f1f] text-gray-500',
};

const MODES = [
  ['championship', 'Championship', '#key'],
  ['round', 'This round', '#round'],
  ['permutations', 'Permutations', '#perms'],
];
const modeFromHash = () => (MODES.find(m => m[2] === window.location.hash)?.[0]) || 'championship';

function KeyInfoTab({ view }) {
  const champs = view.championships;
  const [key, setKey] = useState(null);
  const [mode, setModeState] = useState(modeFromHash);
  const liveKey = champs.find(c => c.live)?.key;
  const current = champs.find(c => c.key === (key || liveKey)) || champs[0];

  const setMode = (m) => {
    setModeState(m);
    window.history.replaceState(null, '', MODES.find(x => x[0] === m)[2]);
  };

  if (!champs.length) return <p className="text-sm text-gray-500">No championships are set for this event.</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {champs.map(c => (
          <button
            key={c.key}
            onClick={() => setKey(c.key)}
            className={`px-4 py-1.5 rounded text-sm font-semibold border ${c.key === current.key ? 'bg-white text-black border-white' : 'bg-[#121212] text-gray-300 border-[#2a2a2a] hover:border-[#444]'}`}
          >
            {c.label}{c.live && <span className="ml-1.5 text-[10px] text-green-500">●</span>}
          </button>
        ))}
        <div className="ml-auto flex rounded border border-[#2a2a2a] overflow-hidden text-sm">
          {MODES.map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} className={`px-4 py-1.5 ${mode === m ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-400 hover:text-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded border border-[#262626] bg-[#121212] px-4 py-3">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs text-gray-500">{current.name}</div>
            <div className={`text-xl font-bold mt-0.5 ${current.title.decided ? 'text-yellow-400' : 'text-white'}`}>
              {current.title.decided ? `🏆 ${current.title.champion.name || `#${current.title.champion.no}`}: GUARANTEED CHAMPION` : current.headline}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {current.live ? `Live: ${current.race}. Everything below is as if it ended now.` : 'No race of this championship on track: standings as they stand.'}
            </div>
          </div>
          {current.season.configured && (
            <div className="ml-auto grid grid-cols-3 gap-4 text-center">
              <Fact label="Points left" value={current.season.remainingPoints} />
              <Fact label="Races left" value={current.season.motosLeft} hint={`${current.season.motosLeftToday} today`} />
              <Fact label="Can still win" value={current.title.contenders} />
            </div>
          )}
        </div>
        {!current.season.configured && (
          <p className="text-xs text-amber-500 mt-2">Rounds remaining is not set for {current.label}, so nothing is said about who can still win.</p>
        )}
      </div>

      {mode === 'championship' && <ChampionshipTable champ={current} />}
      {mode === 'round' && <RoundTable champ={current} />}
      {mode === 'permutations' && <Permutations key={current.key} champ={current} />}
    </div>
  );
}

function Fact({ label, value, hint }) {
  return (
    <div>
      <div className="text-2xl font-bold tabular-nums text-white">{value ?? '-'}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      {hint && <div className="text-[10px] text-gray-600">{hint}</div>}
    </div>
  );
}

function ChampionshipTable({ champ }) {
  const anyBanked = champ.table.some(r => r.banked > 0);
  return (
    <div className="border border-[#262626] rounded overflow-hidden overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-[#161616] text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-2 py-2 text-left w-16">Live pos</th>
            <th className="px-2 py-2 text-left">Rider</th>
            <th className="px-2 py-2 text-right" title="The loaded championship table, before this meeting">Official</th>
            {champ.dayScored
              ? <>
                <th className="px-2 py-2 text-right" title="Race points scored today so far, the banked races and the one on track">Day pts</th>
                <th className="px-2 py-2 text-right" title="Championship points for today's day position if the day ended now">Today</th>
              </>
              : <>
                {anyBanked && <th className="px-2 py-2 text-right" title="Races already banked at this meeting">Earlier today</th>}
                <th className="px-2 py-2 text-right" title="Points for where they are in the race on track">This race</th>
              </>}
            <th className="px-2 py-2 text-right">Live total</th>
            <th className="px-2 py-2 text-right">Gap</th>
            <th className="px-2 py-2 text-right">Max possible</th>
            <th className="px-2 py-2 text-left w-28">Title</th>
          </tr>
        </thead>
        <tbody>
          {champ.table.map((r, i) => (
            <tr key={r.no} className={`border-t border-[#1f1f1f] ${r.title === 'out' ? 'text-gray-600' : ''} ${i % 2 ? 'bg-[#0d0d0d]' : ''}`}>
              <td className="px-2 py-1 tabular-nums font-semibold whitespace-nowrap">{r.pos}{r.tied && '='} <Change change={r.change} /></td>
              <td className="px-2 py-1 truncate max-w-[16rem]">
                <span className="text-gray-500 tabular-nums mr-1.5">{r.no}</span><span className={r.title === 'out' ? '' : 'text-gray-100'}>{r.name}</span>
                {r.bonus ? <span className={`ml-1.5 text-[10px] font-semibold ${r.bonus > 0 ? 'text-amber-400' : 'text-red-400'}`} title="Points added today (Superpole, penalty), included in the totals">{r.bonus > 0 ? '+' : ''}{r.bonus}</span> : null}
              </td>
              <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                {r.official}
                {r.officialPos != null && <span className="ml-1 text-[10px] text-gray-500">P{r.officialPos}</span>}
              </td>
              {champ.dayScored
                ? <>
                  <td className="px-2 py-1 text-right tabular-nums">{r.dayPoints || ''}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-green-400 whitespace-nowrap">
                    {r.race ? `+${r.race}` : ''}
                    {r.dayPos != null && <span className="ml-1 text-[10px] text-gray-500">Day P{r.dayPos}</span>}
                  </td>
                </>
                : <>
                  {anyBanked && <td className="px-2 py-1 text-right tabular-nums">{r.banked ? `+${r.banked}` : ''}</td>}
                  <td className="px-2 py-1 text-right tabular-nums text-green-400 whitespace-nowrap">
                    {r.inRace ? `+${r.race}` : ''}
                    {r.inRace && <span className="ml-1 text-[10px] text-gray-500">P{r.racePos}</span>}
                  </td>
                </>}
              <td className="px-2 py-1 text-right tabular-nums font-semibold text-white">{r.points}</td>
              <td className="px-2 py-1 text-right tabular-nums">{r.gap ? `-${r.gap}` : r.pos > 1 ? 'level' : ''}</td>
              <td className="px-2 py-1 text-right tabular-nums">{r.maxPossible ?? ''}</td>
              <td className="px-2 py-1">
                {r.title && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${TITLE_BADGE[r.title]}`}>{r.title === 'alive' ? 'can win' : r.title}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoundTable({ champ }) {
  const { races, rows } = champ.round;
  if (!rows.length) return <p className="text-sm text-gray-500">No races of {champ.label} yet at this meeting.</p>;
  return (
    <div className="border border-[#262626] rounded overflow-hidden overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]">
        <thead className="bg-[#161616] text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-2 py-2 text-left w-12">Pos</th>
            <th className="px-2 py-2 text-left">Rider</th>
            {races.map((r, i) => (
              <th key={i} className="px-2 py-2 text-right whitespace-nowrap" title={r.groups > 1 ? `${r.groups} groups scored as one race` : undefined}>{r.label}{r.groups > 1 && <span className="ml-1 text-gray-600 normal-case">({r.groups} groups)</span>}{r.live && <span className="ml-1 text-green-500">● live</span>}</th>
            ))}
            <th className="px-2 py-2 text-right">{champ.dayScored ? 'Day pts' : 'Round pts'}</th>
            {champ.dayScored && <th className="px-2 py-2 text-right" title="Championship points for this day position">Champ pts</th>}
            <th className="px-2 py-2 text-right">Gap</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.no} className={`border-t border-[#1f1f1f] ${i % 2 ? 'bg-[#0d0d0d]' : ''}`}>
              <td className="px-2 py-1 tabular-nums font-semibold text-white">{r.pos}</td>
              <td className="px-2 py-1 text-gray-100 truncate max-w-[16rem]"><span className="text-gray-500 tabular-nums mr-1.5">{r.no}</span>{r.name}</td>
              {r.motos.map((m, n) => (
                <td key={n} className={`px-2 py-1 text-right tabular-nums ${m.live ? 'text-green-300' : 'text-gray-300'}`}>
                  {m.pos != null ? <>P{m.pos} <span className="text-gray-500">({m.points})</span></> : '-'}
                </td>
              ))}
              <td className="px-2 py-1 text-right tabular-nums font-semibold text-white">{r.points}</td>
              {champ.dayScored && <td className="px-2 py-1 text-right tabular-nums text-green-400">{r.champPoints ? `+${r.champPoints}` : ''}</td>}
              <td className="px-2 py-1 text-right tabular-nums text-gray-500">{r.gap ? `-${r.gap}` : r.pos > 1 ? 'level' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-3 py-2 text-[11px] text-gray-600 border-t border-[#1f1f1f]">
        Round decided on total points; a tie goes to the better result in the last race{champ.dayScored ? ', then the one before. The day position, not the race points, is what goes into the championship, on the same 35-32-30 scale.' : '.'}
      </p>
    </div>
  );
}

// --- Permutations ----------------------------------------------------------------

const placeRange = ({ from, to }) => (from === 1 && to == null ? 'Anywhere' : to == null ? `P${from} or lower` : from === to ? `P${from}` : `P${from}–P${to}`);
const inLine = (line, place) => place != null && place >= line.from && (line.to == null || place <= line.to);
const finishes = (c) => (c.outsidePoints ? `${c.short} finishes outside the points (P${c.worst} or lower)` : `${c.short} finishes P${c.worst} or lower`);

/**
 * The words for each side of the title: winning it in this race, or keeping
 * it alive into the next one. In the last race of the season only the first
 * exists.
 */
function goalWords(goal, final) {
  if (goal === 'alive') {
    return {
      tab: 'Stay in it',
      verb: 'stay in the title fight',
      result: 'stays in the title fight',
      short: 'still in it',
      outcome: 'STILL IN IT',
      failed: 'out of the title fight',
    };
  }
  return final
    ? { tab: 'Win it', verb: 'win the title', result: 'is champion', short: 'champion', outcome: 'CHAMPION', failed: 'unable to win the title' }
    : { tab: 'Clinch it', verb: 'clinch the title', result: 'clinches the title', short: 'guaranteed champion', outcome: 'GUARANTEED CHAMPION', failed: 'unable to clinch the title' };
}

/** A line of the permutations as one sentence a commentator can read out. */
function lineSentence(line, name, words) {
  if (line.impossible) return `${name} is ${words.failed} from ${placeRange(line)}.`;
  if (!line.conditions.length) {
    if (line.from !== 1) return `${name} ${words.result} by finishing ${placeRange(line)}, whatever anyone else does.`;
    const reach = line.to == null ? 'wherever they finish' : line.to === 1 ? 'by winning the race' : `with a top-${line.to} finish`;
    return `${name} ${words.result} ${reach}, whatever anyone else does.`;
  }
  const where = line.from === 1 && line.to === 1 ? 'by winning the race' : `by finishing ${placeRange(line)}`;
  return `${name} ${words.result} ${where} if ${line.conditions.map(finishes).join(' and ')}.`;
}

/**
 * What each rider still in it needs from the next race: to be sure of the
 * title (Clinch it), and, with races still to come, to keep it alive (Stay in
 * it). Auto follows the race: the championship leader as it stands, with where
 * everyone is right now. Manual: pick any rider, for before and between races.
 */
function Permutations({ champ }) {
  const perms = champ.permutations;
  const [auto, setAuto] = useState(true);
  const [picked, setPicked] = useState(null);
  // Null follows the rider: Clinch it when they can, Stay in it when they cannot.
  const [goalPick, setGoalPick] = useState(null);

  if (!perms) return <p className="text-sm text-amber-500">Set the rounds remaining for {champ.label} to see the title permutations.</p>;
  if (perms.over) return <p className="text-sm text-gray-400">No {champ.label} races left this season.</p>;
  if (!perms.riders.length) return <p className="text-sm text-gray-500">Nobody to show.</p>;

  const leaderNo = champ.table[0]?.no;
  const selected = auto
    ? perms.riders.find(r => r.no === leaderNo) || perms.riders[0]
    : perms.riders.find(r => r.no === picked) || perms.riders[0];
  const pick = (no) => { setAuto(false); setPicked(no); setGoalPick(null); };

  const goal = selected.alive && (goalPick === 'alive' || (goalPick == null && !selected.canClinch)) ? 'alive' : 'clinch';
  const words = goalWords(goal, perms.final);
  const g = goal === 'alive' ? selected.alive : { possible: selected.canClinch, lines: selected.lines, now: selected.now };
  const place = g.now?.place ?? null;
  const within = perms.within ? ` (places among ${champ.label} riders)` : '';
  const stillIn = (r) => r.canClinch || r.alive?.possible;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded border border-[#2a2a2a] overflow-hidden text-sm">
          <button onClick={() => { setAuto(true); setGoalPick(null); }} className={`px-3 py-1.5 ${auto ? 'bg-green-700 text-white' : 'bg-[#121212] text-gray-400 hover:text-gray-200'}`}>
            Auto{perms.live ? ': follow the race' : ''}
          </button>
          <button onClick={() => pick(selected.no)} className={`px-3 py-1.5 ${!auto ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-400 hover:text-gray-200'}`}>
            Manual
          </button>
        </div>
        <span className="text-xs text-gray-500">Pick a rider:</span>
        {perms.riders.map(r => (
          <button
            key={r.no}
            onClick={() => pick(r.no)}
            className={`px-2.5 py-1 rounded text-sm border ${r.no === selected.no ? 'bg-white text-black border-white font-semibold' : stillIn(r) ? 'bg-[#121212] text-gray-200 border-[#2a2a2a] hover:border-[#444]' : 'bg-[#0d0d0d] text-gray-500 border-[#1f1f1f] hover:border-[#333]'}`}
            title={`${r.before} points going into ${perms.unit === 'day' ? 'the round' : 'the race'}`}
          >
            <span className="tabular-nums text-[11px] opacity-60 mr-1">P{r.posBefore}</span>{r.short}
          </button>
        ))}
      </div>

      <div className="rounded border border-[#262626] bg-[#121212] px-4 py-3">
        <div className="flex flex-wrap items-start gap-3">
          <div className="text-xs text-gray-500 flex-1 min-w-[16rem]">
            {auto && <span className="text-green-400 font-semibold mr-1">AUTO</span>}
            For {selected.name} to {words.verb} in {perms.race}{within}.
            {' '}Going in with {selected.before} points
            {perms.unit === 'day'
              ? (perms.final ? '; this is the last round of the season. Places are positions in the day classification.' : `; ${perms.racesAfter} round${perms.racesAfter === 1 ? '' : 's'} (${perms.pointsAfter} points) still to run after it. Places are positions in the day classification.`)
              : (perms.final ? '; this is the last race of the season.' : `; ${perms.racesAfter} race${perms.racesAfter === 1 ? '' : 's'} (${perms.pointsAfter} points) still to run after it.`)}
          </div>
          {selected.alive && (
            <div className="flex rounded border border-[#2a2a2a] overflow-hidden text-sm">
              {['clinch', 'alive'].map(k => (
                <button
                  key={k}
                  onClick={() => setGoalPick(k)}
                  className={`px-3 py-1.5 ${goal === k ? (k === 'alive' ? 'bg-amber-600 text-white' : 'bg-green-700 text-white') : 'bg-[#121212] text-gray-400 hover:text-gray-200'}`}
                >
                  {goalWords(k, perms.final).tab}
                </button>
              ))}
            </div>
          )}
        </div>

        {g.possible && (() => {
          // The one sentence to say: in a race, the line the rider is on now
          // (or, if that line cannot get them there, what they need); otherwise
          // their best case.
          const best = g.lines[0];
          const current = perms.live && place != null ? g.lines.find(l => inLine(l, place)) : null;
          const line = current && !current.impossible ? current : best;
          const behind = current?.impossible || (perms.live && place == null);
          const tone = goal === 'alive' ? 'border-amber-600 bg-amber-950/30' : 'border-green-700 bg-green-950/40';
          return (
            <div className={`mt-2 px-4 py-3 rounded border ${tone}`}>
              {perms.live && (
                <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${goal === 'alive' ? 'text-amber-400' : 'text-green-400'}`}>
                  {place != null ? `Running P${place}` : 'Not running in a scoring place'}{behind ? `. Cannot ${words.verb} from there. Needs:` : ''}
                </div>
              )}
              <div className="text-2xl font-bold text-white leading-snug">{lineSentence(line, selected.short, words)}</div>
            </div>
          );
        })()}

        {perms.live && g.now && (
          <div className={`mt-2 px-3 py-2 rounded text-sm font-semibold ${g.now.met ? (goal === 'alive' ? 'bg-amber-500 text-black' : 'bg-yellow-400 text-black') : 'bg-[#1a1a1a] text-white'}`}>
            {g.now.met
              ? `${goal === 'alive' ? '' : '🏆 '}If it ends now, ${selected.short} ${goal === 'alive' ? 'is still in the title fight' : perms.final ? 'is champion' : 'is guaranteed champion'} (running P${place}).`
              : (
                <>
                  If it ends now, {selected.short} {goal === 'alive' ? 'is out of it' : 'is not yet champion'} (running P{place ?? '-'}).
                  <div className="text-sm font-normal text-gray-300 mt-0.5">
                    {g.now.threats.map(t => (
                      <div key={t.no}>
                        {t.worst == null
                          ? `${t.short} (P${t.place ?? '-'}) is out of reach wherever they finish, so ${selected.short} has to gain places.`
                          : `${t.short} is P${t.place ?? '-'} and needs to drop to P${t.worst} or lower, or ${selected.short} to gain places.`}
                      </div>
                    ))}
                  </div>
                </>
              )}
          </div>
        )}

        {!g.possible ? (
          <p className="mt-3 text-sm text-gray-400">
            {goal === 'alive'
              ? `${selected.short} is out of the title fight after ${perms.race} whatever happens.`
              : `${selected.short} cannot ${words.verb} in ${perms.race}, whatever happens.${perms.final ? '' : ' Switch to "Stay in it" for what keeps the title alive.'}`}
          </p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead className="text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left py-1 w-40">{selected.short} finishes</th>
                <th className="text-left py-1">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {g.lines.map((line) => {
                const now = perms.live && inLine(line, place);
                const good = goal === 'alive' ? 'text-amber-400' : 'text-green-400';
                return (
                  <tr key={line.from} className={`border-t border-[#1f1f1f] ${now ? 'bg-yellow-500/15' : ''}`}>
                    <td className="py-1.5 tabular-nums font-semibold text-white whitespace-nowrap">
                      {placeRange(line)}
                      {now && <span className="ml-2 text-[10px] px-1 rounded bg-yellow-500 text-black font-bold">NOW</span>}
                    </td>
                    <td className="py-1.5">
                      {line.impossible
                        ? <span className="text-gray-500">{goal === 'alive' ? 'out of the title fight' : `cannot ${words.verb} in ${perms.unit === 'day' ? 'this round' : 'this race'}`}</span>
                        : line.conditions.length === 0
                          ? <span className={`${good} font-semibold`}>{words.outcome}, whatever anyone else does</span>
                          : <span className="text-gray-200"><span className={`${good} font-semibold`}>{words.short}</span> if {line.conditions.map(finishes).join(' and ')}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="text-[11px] text-gray-600 mt-2">
          {goal === 'alive'
            ? `Still in it means able to reach the leader with the points left after this ${perms.unit === 'day' ? 'round' : 'race'}. Level counts as still in it.`
            : `Level on points goes to the tie-break, so it is not counted as ${perms.final ? 'winning' : 'clinching'} here.`}
        </p>
      </div>
    </div>
  );
}

// --- The page --------------------------------------------------------------------

/**
 * `load()` returns the view (in-app: the authenticated endpoint; share link:
 * the public one). Polled every second — the endpoint reads memory only.
 */
const KEY_HASHES = ['#key', '#round', '#perms'];

export default function BroadcastTiming({ load, title }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  // #key, #round and #perms open the key-info tab, so each can be bookmarked.
  const [tab, setTab] = useState(() => (KEY_HASHES.includes(window.location.hash) ? 'key' : 'live'));

  useEffect(() => {
    let cancelled = false;
    const tick = () => load()
      .then((data) => { if (!cancelled) { setView(data); setError(''); } })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load live timing'); });
    tick();
    const timer = setInterval(tick, 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [load]);

  useEffect(() => {
    if (tab === 'live') window.history.replaceState(null, '', window.location.pathname);
    else if (!KEY_HASHES.includes(window.location.hash)) window.history.replaceState(null, '', '#key');
  }, [tab]);

  const session = view?.session;
  const heading = useMemo(() => session?.title || view?.show || title || 'Live timing', [session, view, title]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200">
      <header className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b border-[#1f1f1f] px-4 py-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">{session?.event || view?.show || 'Mission Control'} · internal · not for air</div>
            <div className="text-lg font-bold text-white truncate">{heading}</div>
          </div>
          {session && <Flag flag={session.flag} />}
          {session && (
            <div className="flex items-baseline gap-4 tabular-nums">
              {session.clockRemaining && <span className="text-2xl font-bold text-white">{session.clockRemaining}</span>}
              {session.lapsText && <span className="text-sm text-gray-300">{session.lapsText}</span>}
            </div>
          )}
          {session?.bestLap && <FastestLap best={session.bestLap} />}
          <div className="ml-auto flex items-center gap-3">
            <DataAge view={view} error={error} />
            <div className="flex rounded border border-[#2a2a2a] overflow-hidden text-sm">
              {[['live', 'Live timing'], ['key', 'Key info']].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 ${tab === k ? 'bg-white text-black font-semibold' : 'bg-[#121212] text-gray-400 hover:text-gray-200'}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="p-4">
        {!view && !error && <p className="text-sm text-gray-500">Loading…</p>}
        {view && !view.running && (
          <p className="text-sm text-gray-400">Live timing is not running for {view.show || 'this show'} right now. This page picks it up as soon as it starts.</p>
        )}
        {view?.running && tab === 'live' && <LiveTimingTab view={view} />}
        {view?.running && tab === 'key' && <KeyInfoTab view={view} />}
      </main>
    </div>
  );
}

/** The session's fastest lap, big enough to read across a commentary box. */
function FastestLap({ best }) {
  return (
    <div className="flex items-center gap-3 px-3 py-1.5 rounded border border-purple-600 bg-purple-950/50">
      <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 leading-tight">Fastest<br />lap</span>
      <span className="text-2xl font-bold tabular-nums text-purple-200">{best.time}</span>
      <span className="text-sm text-white leading-tight">
        <span className="font-semibold">{best.short || best.name}</span>
        <span className="block text-[11px] text-purple-300 tabular-nums">#{best.no}{best.lap != null ? ` · lap ${best.lap}` : ''}</span>
      </span>
    </div>
  );
}

function DataAge({ view, error }) {
  if (error) return <span className="text-xs text-red-400">{error}</span>;
  if (!view?.running) return null;
  const stale = view.stale;
  return (
    <span className={`flex items-center gap-1.5 text-xs ${stale ? 'text-amber-400' : 'text-gray-500'}`} title="Time since the timing site last answered">
      <span className={`w-2 h-2 rounded-full ${stale ? 'bg-amber-400' : 'bg-green-500'}`} />
      {stale ? `timing ${view.ageSeconds ?? '?'}s old` : 'live'}
      {view.live === false && <span className="text-amber-400"> · {view.note}</span>}
    </span>
  );
}
